import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Heart, Users, Shield, TrendingUp, ArrowRight, Globe, MessageCircle, Trophy, Star, Gift, HandHeart, MapPin, Package, CheckCircle, Clock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer, { ShareSiteButton } from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({ total_users: 0, total_raised: 0, active_posts: 0, total_donations: 0, total_listings: 0 });
  const [latestItems, setLatestItems] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/stats`);
        setStats(response.data);
      } catch { /* ignore */ }
    };
    const fetchLatest = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/community/latest`);
        setLatestItems(response.data.items || []);
      } catch { /* ignore */ }
    };
    fetchStats();
    fetchLatest();
  }, []);

  const features = [
    {
      icon: Heart,
      title: 'Dona con Fiducia',
      titleEn: 'Donate with Trust',
      description: 'Ogni richiesta è verificata. Sai esattamente dove vanno i tuoi soldi.',
      descriptionEn: 'Every request is verified. You know exactly where your money goes.'
    },
    {
      icon: MessageCircle,
      title: 'Comunica Direttamente',
      titleEn: 'Communicate Directly',
      description: 'Chatta con chi aiuti. Costruisci connessioni umane reali.',
      descriptionEn: 'Chat with those you help. Build real human connections.'
    },
    {
      icon: Trophy,
      title: 'Riconoscimento',
      titleEn: 'Recognition',
      description: 'Guadagna badge e scala la classifica dei donatori più generosi.',
      descriptionEn: 'Earn badges and climb the leaderboard of most generous donors.'
    },
    {
      icon: Globe,
      title: 'Impatto Globale',
      titleEn: 'Global Impact',
      description: 'Dona in qualsiasi valuta. Aiuta chiunque, ovunque nel mondo.',
      descriptionEn: 'Donate in any currency. Help anyone, anywhere in the world.'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Crea un Account',
      titleEn: 'Create an Account',
      description: 'Registrati come donatore o come persona in cerca di aiuto.',
      descriptionEn: 'Register as a donor or as someone seeking help.'
    },
    {
      step: '02',
      title: 'Esplora le Richieste',
      titleEn: 'Explore Requests',
      description: 'Naviga tra le storie di chi ha bisogno. Leggi, guarda video, scopri.',
      descriptionEn: 'Browse stories of those in need. Read, watch videos, discover.'
    },
    {
      step: '03',
      title: 'Dona',
      titleEn: 'Donate',
      description: 'Scegli quanto donare. PayPal, carta, bonifico. Semplice e sicuro.',
      descriptionEn: 'Choose how much to donate. PayPal, card, transfer. Simple and secure.'
    },
    {
      step: '04',
      title: 'Vedi l\'Impatto',
      titleEn: 'See the Impact',
      description: 'Ricevi aggiornamenti. Chatta con chi hai aiutato. Festeggia insieme.',
      descriptionEn: 'Get updates. Chat with who you helped. Celebrate together.'
    }
  ];

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1740065592719-052d3e5ec6fb?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Community"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-emerald-900/80 to-emerald-900/95" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <img src="https://customer-assets.emergentagent.com/job_665a114b-bc62-4112-9e11-8ccdfcfc0d6e/artifacts/auplmu5t_1774296304598.png"
                alt="Moneyfor" className="h-16 w-16 rounded-full shadow-lg ring-2 ring-white/30" />
              <span className="font-heading text-4xl font-bold text-white tracking-tight">Moneyfor</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
              {t('heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-stone-200 mb-10 max-w-2xl mx-auto">
              {t('heroSubtitle')}
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto mb-10">
              <form onSubmit={(e) => { e.preventDefault(); const q = e.target.elements.search.value; if(q) window.location.href = `/feed?search=${encodeURIComponent(q)}`; }}
                className="flex items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-full overflow-hidden">
                <input name="search" data-testid="hero-search-input" type="text"
                  placeholder={t('search') + '...'}
                  className="flex-1 px-6 py-4 bg-transparent text-white placeholder-white/60 focus:outline-none text-lg"
                />
                <button type="submit" data-testid="hero-search-btn"
                  className="px-6 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors">
                  {t('search')}
                </button>
              </form>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/feed">
                <Button
                  data-testid="get-started-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40 transition-all hover:scale-105"
                >
                  {language === 'it' ? 'Vai al Feed' : 'Go to Feed'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button
                  data-testid="how-it-works-btn"
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm"
                >
                  {t('howItWorks')}
                </Button>
              </a>
            </div>
            <div className="mt-6">
              <ShareSiteButton />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: `€${stats.total_raised > 0 ? stats.total_raised.toLocaleString() : '0'}`, label: 'Donati', labelEn: 'Donated' },
              { value: `${stats.total_listings || stats.active_posts || 0}`, label: 'Inserzioni attive', labelEn: 'Active listings' },
              { value: `${stats.total_users || 0}`, label: 'Utenti registrati', labelEn: 'Registered users' },
              { value: `${stats.total_donations || 0}`, label: 'Donazioni', labelEn: 'Donations' }
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl font-bold text-emerald-800" data-testid={`stat-${i}`}>{stat.value}</div>
                <div className="text-stone-600 mt-1">{language === 'it' ? stat.label : stat.labelEn}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 tracking-tight mb-4">
              Perché Moneyfor?
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Una piattaforma costruita sulla fiducia, trasparenza e connessione umana
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all border border-stone-100 hover:border-emerald-500/30"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-800/10 flex items-center justify-center mb-5">
                  <feature.icon className="h-6 w-6 text-emerald-800" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3">{feature.title}</h3>
                <p className="text-stone-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 tracking-tight mb-4">
              {t('howItWorks')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Quattro semplici passi per fare la differenza
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
              >
                <div className="text-6xl font-heading font-bold text-emerald-800/10 mb-4">{item.step}</div>
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3">{item.title}</h3>
                <p className="text-stone-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mercatino Solidale CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-teal-700 to-emerald-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-teal-600/20 rounded-full -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-600/20 rounded-full translate-y-1/3 -translate-x-1/3" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Badge className="bg-white/15 text-white border-white/20 mb-4">
                <Package className="h-3.5 w-3.5 mr-1.5" />
                {language === 'it' ? 'Novità' : 'New'}
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                {language === 'it' ? 'Mercatino Solidale' : 'Solidarity Marketplace'}
              </h2>
              <p className="text-teal-100 text-lg mb-6 leading-relaxed">
                {language === 'it'
                  ? 'Regala oggetti che non usi più o trova ciò di cui hai bisogno. Tutto gratis, tutto solidale. Con ricerca per città.'
                  : 'Give away items you no longer use or find what you need. Everything free, everything solidary. With city search.'}
              </p>
              <Link to="/marketplace">
                <Button className="bg-white text-teal-800 hover:bg-teal-50 rounded-full px-8 py-3 font-semibold shadow-lg hover:scale-105 transition-all" data-testid="landing-marketplace-btn">
                  {language === 'it' ? 'Vai al Mercatino' : 'Go to Marketplace'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="grid grid-cols-2 gap-4">
              {[
                { icon: Gift, label: language === 'it' ? 'Regala oggetti' : 'Give items', color: 'bg-amber-400/20' },
                { icon: HandHeart, label: language === 'it' ? 'Ricevi in dono' : 'Receive gifts', color: 'bg-emerald-400/20' },
                { icon: MapPin, label: language === 'it' ? 'Cerca per città' : 'Search by city', color: 'bg-blue-400/20' },
                { icon: MessageCircle, label: language === 'it' ? 'Chatta subito' : 'Chat now', color: 'bg-purple-400/20' }
              ].map((item, i) => (
                <div key={item.label} className={`${item.color} backdrop-blur-sm rounded-2xl p-5 text-center border border-white/10`}>
                  <item.icon className="h-7 w-7 text-white mx-auto mb-2" />
                  <p className="text-white/90 text-sm font-medium">{item.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Community Latest Section */}
      {latestItems.length > 0 && (
        <section className="py-16 md:py-20 bg-stone-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 tracking-tight mb-3">
                {language === 'it' ? 'Dalla Community' : 'From the Community'}
              </h2>
              <p className="text-stone-600 max-w-xl mx-auto">
                {language === 'it' ? 'Le ultime inserzioni pubblicate dai nostri utenti' : 'The latest listings published by our users'}
              </p>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {latestItems.slice(0, 8).map((item, i) => {
                const sectionLabels = { feed: { it: 'Richiesta', en: 'Request', color: 'bg-amber-500' }, marketplace: { it: 'Mercatino', en: 'Market', color: 'bg-teal-500' }, wishes: { it: 'Desiderio', en: 'Wish', color: 'bg-rose-500' }, time: { it: 'Tempo', en: 'Time', color: 'bg-violet-500' } };
                const sec = sectionLabels[item.section] || sectionLabels.feed;
                return (
                  <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                    <Link to={item.link} className="block group" data-testid={`community-item-${item.id}`}>
                      <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden hover:shadow-md transition-all">
                        {item.image ? (
                          <div className="aspect-[4/3] overflow-hidden relative">
                            <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                            <div className={`absolute top-2 left-2 ${sec.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>
                              {language === 'it' ? sec.it : sec.en}
                            </div>
                          </div>
                        ) : (
                          <div className="aspect-[4/3] bg-stone-100 dark:bg-stone-700 flex items-center justify-center relative">
                            <Heart className="h-10 w-10 text-stone-300" />
                            <div className={`absolute top-2 left-2 ${sec.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>
                              {language === 'it' ? sec.it : sec.en}
                            </div>
                          </div>
                        )}
                        <div className="p-3">
                          <h4 className="font-semibold text-stone-900 dark:text-white text-sm line-clamp-1 group-hover:text-emerald-700 transition-colors">{item.title}</h4>
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{item.user_name}</p>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Success Stories + Trust */}
      <section className="py-16 md:py-20 bg-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Star className="h-10 w-10 text-amber-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-stone-900 tracking-tight mb-3">
              {language === 'it' ? 'Storie di Successo' : 'Success Stories'}
            </h2>
            <p className="text-stone-600 mb-6 max-w-lg mx-auto">
              {language === 'it' ? 'Scopri come le donazioni hanno cambiato vite reali nella nostra community' : 'Discover how donations changed real lives in our community'}
            </p>
            <Link to="/success-stories">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-3 font-semibold" data-testid="landing-success-stories-btn">
                {language === 'it' ? 'Leggi le storie' : 'Read the stories'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-t border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 text-stone-400">
            {[
              { icon: Shield, text: language === 'it' ? 'Pagamenti sicuri' : 'Secure payments' },
              { icon: CheckCircle, text: language === 'it' ? 'Identità verificate' : 'Verified identities' },
              { icon: Globe, text: language === 'it' ? '6 lingue supportate' : '6 languages supported' },
              { icon: Heart, text: language === 'it' ? 'Community solidale' : 'Solidarity community' }
            ].map((item, i) => (
              <div key={item.text} className="flex items-center gap-2 text-sm">
                <item.icon className="h-5 w-5 text-emerald-600" />
                <span className="text-stone-600 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-emerald-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-emerald-400 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white tracking-tight mb-6">
              Pronto a fare la differenza?
            </h2>
            <p className="text-emerald-100 text-lg mb-10 max-w-xl mx-auto">
              Unisciti a migliaia di donatori che stanno cambiando vite ogni giorno
            </p>
            <Link to="/feed">
              <Button
                data-testid="cta-join-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40 transition-all hover:scale-105"
              >
                {language === 'it' ? 'Vai al Feed' : 'Go to Feed'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
